import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api';
import { channelLabel } from '../../channels';
import { useOrg, type Kind } from '../Org';

interface CandidateUser {
  id: string;
  username: string;
  name: string;
  email: string;
  active: boolean;
}

/**
 * The Salesforce users Kruvi Access may act as in this org.
 *
 * Called Salesforce users in the menu rather than identities, because that is what an admin is choosing:
 * which Salesforce logins this org offers. Who may use them is the Access grants page, and keeping the two
 * apart is the point - adding a login and handing it to somebody are different decisions with different
 * blast radii.
 */
export function OrgConnections() {
  const { tenantId, orgId, detail, canManage, orgDisabled, members, busy, act, reconnect, setError } = useOrg();
  const [candidates, setCandidates] = useState<CandidateUser[] | null>(null);
  const [kind, setKind] = useState<Kind>('shared');
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState('');

  async function loadCandidates() {
    setError(null);
    try {
      const r = await api<{ users: CandidateUser[] }>(`/api/tenants/${tenantId}/orgs/${orgId}/candidate-users`);
      setCandidates(r.users);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load users');
    }
  }

  function addIdentity(e: FormEvent) {
    e.preventDefault();
    void act(
      () =>
        api(`/api/tenants/${tenantId}/orgs/${orgId}/identities`, {
          method: 'POST',
          body: { username, kind, userId: kind === 'personal' ? userId : undefined },
        }),
      'Salesforce user added and a test login succeeded.',
    ).then(() => {
      setUsername('');
      setUserId('');
    });
  }

  return (
    <>
      <p className="small muted" style={{ marginTop: 0 }}>
        A shared user is a support login everyone works through; a personal one is a named consultant, and
        shows as themselves in the org’s login history. Disable one to pause it; remove one that is gone for
        good. Either way the sessions it minted stay in the session log.
      </p>

      <div className="card flush">
        <table>
          <thead>
            <tr>
              <th>Salesforce user</th>
              <th>Kind</th>
              <th>Verified</th>
              {canManage && <th className="actions-cell"></th>}
            </tr>
          </thead>
          <tbody>
            {detail.identities.length === 0 && (
              <tr>
                <td colSpan={canManage ? 4 : 3} className="empty">
                  No Salesforce users yet.
                </td>
              </tr>
            )}
            {detail.identities.map((i) => (
              <tr key={i.id}>
                <td>
                  {i.username}
                  {i.kind === 'personal' && i.userEmail ? (
                    <>
                      <br />
                      <span className="small muted">{i.userEmail}</span>
                    </>
                  ) : null}
                  {/* The licence is what decides which channels this user can serve at all, so say so here
                      rather than leaving an admin to wonder why a box is greyed out on the Access grants page. */}
                  <br />
                  <span className="small muted">
                    {i.licenseName ?? 'licence not read yet'} · can serve {i.channels.map(channelLabel).join(', ')}
                  </span>
                  {canManage && !i.licenseName && !i.disabledAt && (
                    <div style={{ marginTop: 6 }}>
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={() =>
                          act(
                            () =>
                              api(`/api/tenants/${tenantId}/orgs/${orgId}/identities/${i.id}/recheck`, {
                                method: 'POST',
                              }),
                            'Licence read. The channels this Salesforce user can serve are up to date.',
                          )
                        }
                      >
                        Re-check licence
                      </button>
                    </div>
                  )}
                </td>
                <td>{i.kind}</td>
                <td>
                  {i.disabledAt ? (
                    <span className="pill off">disabled</span>
                  ) : i.verified ? (
                    <span className="pill on">yes</span>
                  ) : (
                    <span className="pill off" title={i.lastMintError ?? ''}>
                      failed
                    </span>
                  )}
                </td>
                {canManage && (
                  <td className="actions-cell">
                    {i.disabledAt ? (
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={() =>
                          act(
                            () =>
                              api(`/api/tenants/${tenantId}/orgs/${orgId}/identities/${i.id}/enable`, {
                                method: 'POST',
                              }),
                            'Salesforce user re-enabled. Grant access again to make it usable.',
                          )
                        }
                      >
                        Enable
                      </button>
                    ) : (
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={() =>
                          act(
                            () =>
                              api(`/api/tenants/${tenantId}/orgs/${orgId}/identities/${i.id}/disable`, {
                                method: 'POST',
                              }),
                            'Salesforce user disabled and its grants revoked. Any live session ends within a minute.',
                          )
                        }
                      >
                        Disable
                      </button>
                    )}{' '}
                    {/* Removal is for a user that is gone rather than paused — deleted outright when it
                        never minted anything, hidden but kept when the session log names it. The red one
                        of the pair, because disabling can be undone and this cannot. */}
                    <button
                      className="danger"
                      disabled={busy}
                      onClick={() => {
                        if (
                          !window.confirm(
                            `Remove ${i.username}? It disappears from this org and cannot be brought back.`,
                          )
                        )
                          return;
                        void act(
                          () =>
                            api(`/api/tenants/${tenantId}/orgs/${orgId}/identities/${i.id}`, { method: 'DELETE' }),
                          'Salesforce user removed. Sessions it minted stay in the session log.',
                        );
                      }}
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canManage && !orgDisabled && (
        <div className="card">
          <h2>Add a Salesforce user</h2>
          {!detail.setupReady ? (
            <>
              <p>
                To add a Salesforce user, sign in to the org again so Kruvi Access can list its users and run a
                test login. The token from the last sign-in has expired, which is by design — it is held for
                minutes, not kept.
              </p>
              <button onClick={reconnect} disabled={busy}>
                Sign in to {detail.org.label}
              </button>
            </>
          ) : (
            <>
              <div className="row" style={{ marginBottom: 16 }}>
                <button className="secondary" onClick={loadCandidates} disabled={busy}>
                  Load eligible users
                </button>
                <span className="small muted">Users holding the Kruvi Access permission set.</span>
              </div>
              {candidates && candidates.length === 0 && (
                <div className="notice">
                  No users in that org hold the Kruvi Access permission set yet. Assign it in Salesforce,
                  then load again.
                </div>
              )}
              <form onSubmit={addIdentity}>
                <label>
                  Kind
                  <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                    <option value="shared">Shared: a support login everyone works through</option>
                    <option value="personal">Personal: one named person, who appears as themselves</option>
                  </select>
                </label>
                <label>
                  Salesforce user
                  {candidates && candidates.length > 0 ? (
                    <select value={username} onChange={(e) => setUsername(e.target.value)} required>
                      <option value="">Choose a user…</option>
                      {candidates.map((u) => (
                        <option key={u.id} value={u.username}>
                          {u.name} — {u.username}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      placeholder="support@customer.com"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  )}
                </label>
                {kind === 'personal' && (
                  <label>
                    Which member of the workspace is it?
                    <select value={userId} onChange={(e) => setUserId(e.target.value)} required>
                      <option value="">Choose a person…</option>
                      {members.map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.displayName || m.email}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <button type="submit" className="block" disabled={busy || !username}>
                  Add and test the login
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
